import { useEffect } from 'react';
import { Star, CheckSquare, Calendar } from 'lucide-react';
import { cn } from '../../../../utils/cn';
import { formatDDMMYYYY } from '../../../../utils/timeUtils';
import { getInitials } from '../../../common/Table2';
import { avatarGradientToken } from '../../../../utils/surveyInsights';
import { useLazyGetResponseDetailQuery } from '../../../../store/apis/survey.api';
import type { ResponseAnswer } from '../types';
import type { RespondentSelection } from './RespondentsPanel';

interface RespondentAnswersProps {
  selection: RespondentSelection | null;
}

const QUESTION_TYPE_LABELS: Record<string, string> = {
  rating: 'Rating',
  mcq: 'Single choice',
  checkbox: 'Multi select',
  short_text: 'Free text',
  long_text: 'Free text',
  date: 'Date',
  number: 'Number',
};

/** Figma answer panel (dc.html:3027): respondent header + per-answer blocks. */
const RespondentAnswers: React.FC<RespondentAnswersProps> = ({ selection }) => {
  const [fetchResponse, { data: response, isLoading, isError }] = useLazyGetResponseDetailQuery();

  useEffect(() => {
    if (selection?.kind === 'completed' && selection.id) {
      fetchResponse({ responseId: selection.id });
    }
  }, [selection, fetchResponse]);

  if (!selection) {
    return (
      <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-card shadow-[var(--shadow-sm)] p-0 overflow-hidden">
        <div className="text-center py-16 px-6">
          <p className="text-body-md font-bold text-[var(--color-text-primary)]">Select a respondent</p>
          <p className="text-body-xs text-[var(--color-text-secondary)] mt-1">
            Choose someone from the list to review their answers.
          </p>
        </div>
      </div>
    );
  }

  const done = selection.kind === 'completed';

  return (
    <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-card shadow-[var(--shadow-sm)] p-0 overflow-hidden">
      <div className="flex items-center gap-3 px-[22px] py-[18px] border-b border-[var(--color-border-tertiary)]">
        <span
          className={`w-[42px] h-[42px] rounded-[12px] flex-none grid place-items-center text-white font-bold text-[13px] ${avatarGradientToken(selection.name)}`}
        >
          {getInitials(selection.name)}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-heading-sm text-[var(--color-text-primary)] truncate">
            {selection.name}
          </div>
          <div className="text-caption text-[var(--color-text-tertiary)] truncate">
            {selection.sub} · {selection.submittedLabel}
          </div>
        </div>
        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-caption font-bold whitespace-nowrap flex-none ${
            done
              ? "bg-[var(--color-success-light)] text-[var(--color-success-dark)]"
              : "bg-[var(--color-warning-light)] text-[var(--color-warning-dark)]"
          }`}
        >
          {done ? 'Complete' : 'Partial'}
        </span>
      </div>

      <div className="px-[22px] pt-1.5 pb-[18px]">
        {!done ? (
          <div className="text-center py-12">
            <p className="text-body-md font-bold text-[var(--color-text-primary)]">No answers submitted yet</p>
            <p className="text-body-xs text-[var(--color-text-secondary)] mt-1">
              This respondent has not completed the survey.
            </p>
          </div>
        ) : isLoading ? (
          <div className="py-6 text-body-sm text-[var(--color-text-tertiary)]">Loading answers…</div>
        ) : isError ? (
          <div className="py-6 text-body-sm text-[var(--color-error-dark)]">
            Failed to load answers. Please try again.
          </div>
        ) : response?.answers && response.answers.length > 0 ? (
          response.answers.map((answer: ResponseAnswer, index: number) => (
            <AnswerBlock
              key={answer.questionId}
              answer={answer}
              index={index}
              isLast={index === response.answers.length - 1}
            />
          ))
        ) : (
          !isLoading && (
            <div className="text-center py-12">
              <p className="text-body-sm text-[var(--color-text-tertiary)] italic">No answers found for this response.</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

const AnswerBlock: React.FC<{ answer: ResponseAnswer; index: number; isLast: boolean }> = ({
  answer,
  index,
  isLast,
}) => {
  const type = answer?.questionType ?? 'short_text';
  const rawAnswer = answer?.answer;
  const isEmpty =
    rawAnswer === undefined ||
    rawAnswer === null ||
    rawAnswer === '' ||
    (Array.isArray(rawAnswer) && rawAnswer.length === 0);

  const renderValue = () => {
    if (isEmpty) return null;
    switch (type) {
      case 'rating': {
        const rating = typeof rawAnswer === 'number' ? rawAnswer : parseInt(String(rawAnswer), 10) || 0;
        const maxRating = answer?.maxRating || 5;
        return (
          <div className="flex items-center gap-1 mt-1">
            {Array.from({ length: maxRating }).map((_, i) => (
              <Star
                key={i}
                className={cn(
                  'w-5 h-5',
                  i < rating
                    ? 'fill-[var(--color-warning)] text-[var(--color-warning)]'
                    : 'fill-[var(--color-bg-subtle)] text-[var(--color-border-primary)]'
                )}
              />
            ))}
            <span className="text-body-sm text-[var(--color-text-tertiary)] ml-2 font-medium">
              ({rating}/{maxRating})
            </span>
          </div>
        );
      }
      case 'checkbox': {
        const selections = Array.isArray(rawAnswer) ? rawAnswer : [String(rawAnswer)];
        return (
          <div className="mt-1 space-y-2">
            {selections.map((opt: string, i: number) => (
              <div key={i} className="flex items-center gap-3 text-[var(--color-text-strong)]">
                <CheckSquare className="w-5 h-5 text-[var(--color-success-dark)]" />
                <span className="text-body-sm">{opt}</span>
              </div>
            ))}
          </div>
        );
      }
      case 'mcq':
        return (
          <div className="mt-1 inline-flex items-center px-3 py-1.5 rounded-full bg-[var(--color-primary-100)] text-[var(--color-primary-700)] text-body-sm font-medium">
            {String(rawAnswer)}
          </div>
        );
      case 'date': {
        const dateVal = rawAnswer ? new Date(String(rawAnswer)) : null;
        const formatted =
          dateVal && !isNaN(dateVal.getTime())
            ? formatDDMMYYYY(String(rawAnswer))
            : String(rawAnswer);
        return (
          <div className="mt-1 inline-flex items-center gap-2 text-body-sm text-[var(--color-text-primary)] font-medium bg-[var(--color-bg-muted)] px-3 py-1.5 rounded-button">
            <Calendar className="w-4 h-4 text-[var(--color-text-tertiary)]" />
            {formatted}
          </div>
        );
      }
      case 'short_text':
      case 'long_text':
        return (
          <div className="mt-1 bg-[var(--color-bg-muted)] rounded-[12px] p-3 text-body-sm text-[var(--color-text-strong)]">
            {String(rawAnswer)}
          </div>
        );
      default:
        return <p className="mt-1 text-body-sm text-[var(--color-text-strong)]">{String(rawAnswer)}</p>;
    }
  };

  return (
    <div className={cn('py-4', !isLast && 'border-b border-[var(--color-border-tertiary)]')}>
      <div className="flex items-start gap-[11px] mb-[9px]">
        <span className="w-6 h-6 rounded-lg flex-none grid place-items-center bg-[var(--color-bg-muted)] text-[var(--color-text-secondary)] text-caption font-extrabold">
          {index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-label font-semibold text-[var(--color-text-secondary)] leading-[1.4]">
            {answer?.questionText ?? ''}
          </div>
        </div>
        <span className="inline-flex items-center px-2 py-[3px] rounded-full text-[10.5px] font-bold bg-[var(--color-bg-muted)] text-[var(--color-text-secondary)] whitespace-nowrap flex-none">
          {QUESTION_TYPE_LABELS[type] ?? type}
        </span>
      </div>
      {isEmpty ? (
        <div className="ml-[35px] text-label italic text-[var(--color-layout-header-icon-secondary)] font-semibold">
          Not answered
        </div>
      ) : (
        <div className="ml-[35px]">{renderValue()}</div>
      )}
    </div>
  );
};

export default RespondentAnswers;
